import { Fragment, useEffect } from 'react';
import { ahuComponentFormsApi } from 'app/services/ahuComponentFormsApi';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { formatDecimalDisplay } from '../formUtils';
import { useComponentForm } from '../useComponentForm';
import { cmsList, FormSubtab, handleTabToNext } from '../formKit';
import { useJsonDiffEmit } from '../jsonDiffEmit';
import {
  BlankOffSlot,
  useAirStreamRows,
  FormHeading,
  MBareInput,
  MBareSelect,
  MInput,
  MSelect,
  PSelect,
  RequiredFieldNote,
  SBareInput,
  HeadingChrome,
} from './formCommon';
import type { ComponentFormApi } from '../useComponentForm';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — EVAP PACK form (§50.1 row; legacy form.js 180 + form.tmpl 584;
 * parity 323): 5 catalogue fields (media/pump/fill/drain/overflow);
 * `draw:evap_pack:connection (data, section.id)` — THE G32 feed — built from
 * fill/drain/overflow conn geometry behind a JSON-diff snapshot (#334-style);
 * `change:tunnel:widthOrHeight` → 1 s fetch + ladb/lawb in-place patch;
 * delim_depth_id checkbox special (checked → first CMS depth id, unchecked →
 * null — §49.3 field binding).
 */
const config = COMPONENT_FORM_CONFIGS.evap_pack;

/**
 * "Connection Hand" — the `.checkboxes-group` radio row legacy renders under
 * both Fill (form.tmpl :377-392) and Drain (:451-468). Same markup the evap
 * SELECTOR already uses for its Hand rows.
 */
const ConnHandRow = ({
  api,
  field,
  cmsPath,
  /** legacy stamps tabindex="-1" on Fill's radios only (:385 vs :456). */
  untabbable,
}: {
  api: ComponentFormApi;
  field: string;
  cmsPath: string;
  untabbable?: boolean;
}) => {
  const current = String(api.model?.get(field) ?? '');
  return (
    <li>
      <div>
        <div className="checkboxes-group">
          {/* §JSX-WS — legacy's `{{#each}}` puts a newline between the radio
              and its text (form.tmpl :382-386) and JSX drops it, so "LH"/"RH"
              sit 3.2px closer to their radio than on dev. The `.radio` boxes
              themselves do NOT move (styles.css:2818 fixes them at 46.27%
              with the second floated right) — only the text inside, which is
              exactly what the row looks wrong for. The space after `</label>`
              is inert for the same reason; it is here to match the tmpl. */}
          {cmsList(api.componentCms, cmsPath).map((r) => (
            <Fragment key={String(r.id)}>
              <label className="radio">
                <input
                  type="radio"
                  className="model_field"
                  name={field}
                  value={String(r.id)}
                  checked={String(r.id) === current}
                  onChange={() => api.commitModelField(field, String(r.id))}
                  tabIndex={untabbable ? -1 : undefined}
                />{' '}
                {String(r.name ?? '')}{' '}
              </label>{' '}
            </Fragment>
          ))}
        </div>
        <span className="label">Connection Hand</span>
      </div>
    </li>
  );
};

const EvapPackForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'media',
  });
  const { model } = api;
  // ★ Hoisted above every render-path return in this component: a hook
  // that is not reached on each render changes the hook count and React
  // throws #310. That broke the AHU board twice (e5f5bce64e, 3d655d33af),
  // both times past a clean type check. `scripts/hook-order` guards it.
  const airStreamRows = useAirStreamRows(cms, model?.get('air_stream_id'));
  const dispatch = useAppDispatch();
  const emitIfChanged = useJsonDiffEmit();

  // drawConnections() — G32 feed, JSON-diff gated (every render in legacy).
  useEffect(() => {
    if (!model) return;
    const payload = {
      fill: {
        conn_hand: model.get('fill_conn_hand_id'),
        nipple: model.get('fill_id'),
      },
      drain: {
        conn_hand: model.get('drain_conn_hand_id'),
        nipple: model.get('drain_id'),
      },
      overflow: { nipple: model.get('overflow_id') },
    };
    emitIfChanged(`evap:${row.id}`, payload, () => {
      ahuBoardBus.emit('draw:evap_pack:connection', payload, section.id);
    });
  });

  // change:tunnel:widthOrHeight → updateModel (1 s fetch + ladb/lawb patch).
  useEffect(() => {
    if (!model) return undefined;
    return ahuBoardBus.on('change:tunnel:widthOrHeight', () => {
      window.setTimeout(() => {
        void dispatch(
          ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
            { plural: config.plural, id: row.id },
            { subscribe: false, forceRefetch: true }
          )
        )
          .unwrap()
          .then((fresh) =>
            model.set(
              { ladb_temp: fresh.ladb_temp, lawb_temp: fresh.lawb_temp },
              { xhr: true }
            )
          )
          .catch(() => undefined);
      }, 1000);
    });
  }, [model, dispatch, row.id]);

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  const pumpParams = (() => {
    try {
      return JSON.parse(String(model.get('pump_params') ?? '{}')) as Record<string, unknown>;
    } catch {
      return {} as Record<string, unknown>;
    }
  })();

  // Legacy narrows each valve select to the ids the row exposes for the
  // CURRENTLY selected fill/drain (component_view.js:472-515): the tmpl reads
  // the bare `fill_float_valve` / `fill_automatic_valve` / `drain_automatic_valve`
  // template vars, NOT `componentCms.<x>_params.<valve>`. When the `_ids`
  // property is empty the var is never assigned, so `{{#each}}` renders no
  // options at all — hence the `?? []` rather than a fallback to the full list.
  const valveOptions = (cmsPath: string, idsField: string) => {
    const ids = model.get(idsField);
    if (!Array.isArray(ids)) return [];
    const wanted = new Set(ids.map((id) => String(id)));
    return cmsList(api.componentCms, cmsPath).filter((r) => wanted.has(String(r.id)));
  };

  // delim_depth_id checkbox special: checked → FIRST CMS depth id (§49.3).
  const delimDepths = cmsList(api.componentCms, 'drift_eliminator.depth');
  const onDelimCheck = (checked: boolean) => {
    api.commitModelField(
      'delim_depth_id',
      checked && delimDepths.length ? String(delimDepths[0].id) : null
    );
  };

  return (
    <>
      <FormHeading
        title="evap pack"
        api={api}
        wrapperClass="component-form-heading clearfix hide"
        chrome={config.catalogueFields.map((cf) => ({
          field: cf,
          visClass: `vis-${cf.componentName}`,
          searchResultClass: 'search-result',
        }))}
        // NO rating-mode label: the legacy evap_pack form does NOT set
        // withRating (unlike fan/coil), so the base renders `.rate-active-mode
        // hide` and the tabbed evap selector is unreachable to OLD users. The
        // EvapPackSelector is ported but stays dormant (registered, no opener)
        // to match the live OLD board — the orphaned legacy selector from the
        // unmerged origin/Evap-Pack-Selection-Dialog branch. Wiring a visible
        // opener would expose UX OLD users never had (parity break). Re-enable
        // only when that branch ships and product signs off.
      />
      <form onKeyDown={handleTabToNext}>
        {/* Every subtab body carries `non-clickable` because legacy's does:
            evap_pack form.tmpl renders all seven of its bodies as
            `<ul class="sublist non-clickable">` (:25,168,210,364,436,498,519)
            and skeleton.css:97 makes that pointer-events:none + opacity .6.
            The whole evap COMPONENT tab is read-only — SELECTION writes it.
            The subtab set is legacy's seven verbatim: Media, Sump,
            Pump & Piping, Fill, Drain, Overflow, Performance. */}
        {/* Every catalogue chrome wrapper carries `hide` because legacy's does
            — form.tmpl :14/:199 `ahu-header-btns right hide` (media, pump) and
            :353/:425/:487 `ahu-header-btns sync-result hide` (fill, drain,
            overflow). Nothing in the legacy view ever lifts it, so the count
            badge / Smartlist button / Reset span are dead markup on OLD, in
            keeping with the read-only COMPONENT tab above. Their config carries
            `noFetchCount: true` (componentFormConfigs.ts) so no counts are
            fetched for chrome nobody can see. */}
        <ul className="inputs-group">
          <FormSubtab type="evap_pack" cid={row.id} name="media" label="Media" open={sub('media')} onToggle={api.toggleSubtab} sublistClass="non-clickable" headerChrome={<HeadingChrome api={api} spec={{ field: config.catalogueFields[0], visClass: 'vis-media' }} wrapperClass="ahu-header-btns right hide" />}>
            {/* No `required` anywhere in this form: legacy evap_pack
                form.tmpl marks not one label (`grep -c 'label required'` = 0),
                unlike eliminator/silencer. `.required:after { content:"*" }`
                (common.css:29077), so a stray prop paints an asterisk dev
                never shows. */}
            <PSelect api={api} target="master_media_id" name="mfg_id" label="Manufacturer" cmsPath="media.manufacturer" />
            <PSelect api={api} target="master_media_id" name="model_id" label="Model" cmsPath="media.model" />
            <PSelect api={api} target="master_media_id" name="edge_finish_id" label="Edge Finish" cmsPath="media.edge_finish" />
            <PSelect api={api} target="master_media_id" name="depth_id" label="Depth" unit="in" cmsPath="media.depth" labelKey="value" />
            {/* §JSX-WS — every `{' '}` below restores a newline the legacy
                tmpl has between two controls. `.unit`, `.multiply-sign`, the
                inputs and the selects are all in-flow inline-blocks
                (styles.css:3483, :4106, :4508), so that collapsed space is
                ~4px of real layout: without it each control after the first
                sits left of dev and the row loses its right-edge alignment
                with the rows above. Same defect and same fix as the fan
                (FanForm.tsx:386) and silencer forms. */}
            <li>
              <div className="multiply single-line">
                <span className="unit">in</span>{' '}
                <MBareInput api={api} name="media_aw_width" readOnly extraClass="medium-input" fieldClass="" />{' '}
                {/* The `X` sits after an `<input>` and carries `.unit` without
                    `.long-unit`, so styles.css:2957 hits it with `width: 24px
                    !important`. Legacy beats that with an IMPORTANT INLINE
                    declaration (form.tmpl :121 `style="width:16px
                    !important"`); React's style prop can only emit a normal
                    one, which loses — the sign rendered 24px and pushed the
                    height input 8px right of dev. Same reason and same shape
                    as GasHeaterRatedOutputs.tsx:435. */}
                <span
                  className="unit multiply-sign"
                  ref={(el) => {
                    el?.style.setProperty('width', '16px', 'important');
                  }}
                >
                  X
                </span>{' '}
                <MBareInput api={api} name="media_aw_height" readOnly extraClass="medium-input" fieldClass="" />{' '}
                <span className="label">Airway W x H</span>
              </div>
            </li>
            <MSelect api={api} name="drip_tray_mtl_gauge_id" label="Wet Metals" cmsPath="drip_tray_mtl_gauge" labelKey="model" hideNone />
            {/* DE Depth: legacy check-select single row — unit + checkbox +
                mirror + (non-clickable until checked) depth select (tmpl 114-131). */}
            <li>
              <div className="check-select single-line">
                <span className="unit">in</span>{' '}
                <input
                  type="checkbox"
                  name="delim_depth_id"
                  className="model_field"
                  checked={model.get('delim_depth_id') != null}
                  onChange={(e) => onDelimCheck(e.target.checked)}
                />{' '}
                <span className="selection" style={{ marginLeft: '45px' }}>
                  {(() => {
                    const hit = delimDepths.find(
                      // eslint-disable-next-line eqeqeq
                      (r) => r.id == model.get('delim_depth_id')
                    );
                    return hit ? <span>{String(hit.value ?? '')}</span> : null;
                  })()}
                </span>{' '}
                <select
                  className={`medium-input model_field${model.get('delim_depth_id') ? '' : ' non-clickable'}`}
                  name="delim_depth_id"
                  value={String(model.get('delim_depth_id') ?? 'None')}
                  onChange={(e) => api.commitModelField('delim_depth_id', e.target.value)}
                >
                  <option value="None" className="hide">- -</option>
                  {delimDepths.map((r) => (
                    <option key={String(r.id)} value={String(r.id)}>
                      {String(r.value ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">DE Depth</span>
              </div>
            </li>
          </FormSubtab>

          {/* Legacy label is "Sump" and the body is TWO rows (form.tmpl
              :164-196). Piping / Splash Pipe Mtl NPS live in Pump & Piping
              below, under its own `Piping` sublist-heading. */}
          <FormSubtab type="evap_pack" cid={row.id} name="sump_piping" label="Sump" open={sub('sump_piping')} onToggle={api.toggleSubtab} sublistClass="non-clickable">
            <MSelect api={api} name="sump_mtl_gauge_id" label="Sump Mtl" cmsPath="sump_mtl_gauge" labelKey="name" />
            <MInput api={api} name="sump_length" label="Sump Length" unit="in" readOnly rowClass="single-line" />
          </FormSubtab>

          {/* form.tmpl :197-350 — label "Pump & Piping"; the pump trio + the
              Power|Voltage pair, then the `Piping` heading and FOUR model
              rows. Time Clock / Freez-Stat are the tail of THIS body
              (:321-348); legacy has no Accessories subtab (its `subtabs`
              array names one, view/form.js:120, but the template renders
              none — a vestigial entry, not a rendered tab). */}
          <FormSubtab type="evap_pack" cid={row.id} name="pump" label="Pump & Piping" open={sub('pump')} onToggle={api.toggleSubtab} sublistClass="non-clickable" headerChrome={<HeadingChrome api={api} spec={{ field: config.catalogueFields[1], visClass: 'vis-pump' }} wrapperClass="ahu-header-btns right hide" />}>
            <PSelect api={api} target="master_pump_id" name="type_id" label="Type" cmsPath="pump.type" />
            <PSelect api={api} target="master_pump_id" name="mfg_id" label="Manufacturer" cmsPath="pump.manufacturer" />
            <PSelect api={api} target="master_pump_id" name="model" label="Model" cmsPath="pump.model" labelKey="model" valueKey="model" />
            <li>
              <div>
                <span className="unit">HP</span>{' '}
                <input type="text" data-formatter_type="decimal" autoComplete="off" className="pump_output medium-input" name="power" value={formatDecimalDisplay(pumpParams.power)} readOnly tabIndex={-1} onChange={() => undefined} />{' '}
                <input type="text" className="pump_output medium-input" name="voltage" value={String(pumpParams.voltage ?? '')} readOnly tabIndex={-1} onChange={() => undefined} />{' '}
                {/* Plain text, no inner spans — legacy's label is one text
                    node (form.tmpl :285-287), unlike EADB / EAWB below. The
                    spans rendered the same but they made `.label > *` real,
                    which arms styles.css:3252's per-half invalid colouring on
                    a row dev cannot colour. */}
                <span className="label">Power | Voltage</span>
              </div>
            </li>
            <li className="sublist-heading">
              <h3>Piping</h3>
            </li>
            <MSelect api={api} name="pipe_mtl_sch_id" label="Piping" cmsPath="piping" labelKey="name" />
            <MSelect api={api} name="spray_pipe_splash_id" label="Splash Pipe Mtl NPS" cmsPath="splash_pipe" labelKey="name" />
            <MSelect api={api} name="time_clock_id" label="Time Clock" cmsPath="timeclock" />
            <MSelect api={api} name="db_temp_sensor_spn_id" label="Freez-Stat" cmsPath="freez_stat" />
          </FormSubtab>

          <FormSubtab type="evap_pack" cid={row.id} name="fill" label="Fill" open={sub('fill')} onToggle={api.toggleSubtab} sublistClass="non-clickable" headerChrome={<HeadingChrome api={api} spec={{ field: config.catalogueFields[2], visClass: 'vis-fill' }} wrapperClass="ahu-header-btns sync-result hide" />}>
            <PSelect api={api} target="fill_id" name="nipple_id" label="Connection" cmsPath="fill" labelKey="nipple" valueKey="nipple_id" />
            <ConnHandRow api={api} field="fill_conn_hand_id" cmsPath="fill_params.connection_hand" untabbable />
            <MSelect api={api} name="fill_float_valve_id" label="Float Valve" options={valveOptions('fill_params.float_valve', 'fill_float_valve_ids')} />
            <MSelect api={api} name="fill_automatic_valve_id" label="Automatic Valve" options={valveOptions('fill_params.automatic_valve', 'fill_automatic_valve_ids')} />
          </FormSubtab>

          <FormSubtab type="evap_pack" cid={row.id} name="drain" label="Drain" open={sub('drain')} onToggle={api.toggleSubtab} sublistClass="non-clickable" headerChrome={<HeadingChrome api={api} spec={{ field: config.catalogueFields[3], visClass: 'vis-drain' }} wrapperClass="ahu-header-btns sync-result hide" />}>
            <PSelect api={api} target="drain_id" name="nipple_id" label="Connection" cmsPath="drain" labelKey="nipple" valueKey="nipple_id" />
            <ConnHandRow api={api} field="drain_conn_hand_id" cmsPath="drain_params.connection_hand" />
            <MSelect api={api} name="drain_automatic_valve_id" label="Automatic Valve" options={valveOptions('drain_params.automatic_valve', 'drain_automatic_valve_ids')} />
          </FormSubtab>

          {/* Overflow is Connection ALONE (form.tmpl :498-513). The view
              BUILDS an `overflow_automatic_valve` list (component_view.js:504)
              but no template ever renders it — dead code, so the Valve row
              here was a React-only row. */}
          <FormSubtab type="evap_pack" cid={row.id} name="overflow" label="Overflow" open={sub('overflow')} onToggle={api.toggleSubtab} sublistClass="non-clickable" headerChrome={<HeadingChrome api={api} spec={{ field: config.catalogueFields[4], visClass: 'vis-overflow' }} wrapperClass="ahu-header-btns sync-result hide" />}>
            <PSelect api={api} target="overflow_id" name="nipple_id" label="Connection" cmsPath="overflow" labelKey="nipple" valueKey="nipple_id" />
          </FormSubtab>

          <FormSubtab type="evap_pack" cid={row.id} name="performance" label="Performance" open={sub('performance')} onToggle={api.toggleSubtab} sublistClass="non-clickable">
            {/* Not the shared <AirStreamFlow>: legacy's evap row (form.tmpl
                :520-538) is a CLASSLESS div (not `single-line`), its select
                carries no `m-0`, and the airflow input is `performance-small`
                (93px, styles.css:1185) with the model's min/max airflow. */}
            <li>
              <div>
                <span className="unit">CFM</span>{' '}
                <MBareSelect api={api} name="air_stream_id" options={airStreamRows} extraClass="medium-input" />{' '}
                <MBareInput
                  api={api}
                  name="airflow_actual"
                  formatter="integer"
                  extraClass="medium-input performance-small"
                  min={Number(model.get('min_airflow') ?? 0) || 0}
                  max={Number(model.get('max_airflow') ?? 0) || 400000}
                />{' '}
                <span className="label">Air Stream &amp; Flow</span>
              </div>
            </li>
            <li>
              <div>
                <span className="unit">fpm</span>{' '}
                {/* legacy has NO data-formatter_type on this one input, so it
                    renders the raw value (229, not 229.00) — form.tmpl :560. */}
                <MBareInput api={api} name="face_velocity_actual" formatter="none" readOnly extraClass="medium-input" />{' '}
                <span className="unit long-unit">in WC</span>{' '}
                <MBareInput api={api} name="media_apd" readOnly extraClass="medium-input performance-small" />{' '}
                {/* Plain text for the same reason as Power | Voltage above —
                    form.tmpl :565-567 is one text node. */}
                <span className="label">Face Velocity | APD</span>
              </div>
            </li>
            <MInput api={api} name="media_clg_eff_percent" label="Effectiveness" unit="%" readOnly extraClass="medium-input" />
            <li>
              <div className="evap-pack-total-airflow">
                <span className="unit temp"> F </span>{' '}
                {/* Dev-sync J4 — renamed to the _summer aliases (evap_pack
                    form.tmpl +586..587; survey §A3a: pure rename, same
                    columns) so useComponentForm's evap-pack commit gate
                    (section.js:268-283) sees the legacy keys. */}
                <SBareInput
                  api={api}
                  name="eadb_temp_summer"
                  min={8}
                  max={120}
                  value={(section as Record<string, unknown>).eadb_temp_summer ?? section.eadb_temp}
                />{' '}
                <SBareInput
                  api={api}
                  name="eawb_temp_summer"
                  min={8}
                  max={120}
                  value={(section as Record<string, unknown>).eawb_temp_summer ?? section.eawb_temp}
                />{' '}
                <span className="label">
                  <span>EADB</span> / <span>EAWB</span>
                </span>
              </div>
            </li>
            <li>
              <div>
                <span className="unit temp"> F </span>{' '}
                <MBareInput api={api} name="ladb_temp" readOnly extraClass="medium-input" />{' '}
                <MBareInput api={api} name="lawb_temp" readOnly extraClass="medium-input" />{' '}
                <span className="label">LADB / LAWB</span>
              </div>
            </li>
            <MInput api={api} name="water_flow" label="Water Flow" unit="gpm" readOnly />
          </FormSubtab>



          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      {/* form.tmpl :622 — the footer sits OUTSIDE the form, as here. */}
      <RequiredFieldNote />
    </>
  );
};

export default EvapPackForm;
